// ─── Tile ordering: Looker's order, exactly ──────────────────────────────────
// One place for "what order do a tile's rows, pivot columns and table columns
// come out in". The rule (tickets #27 / #50 / #126 / #68, all from the same
// complaint — "it doesn't look like Looker"): an imported tile is a COPY of
// the Looker tile, not a reinterpretation of it. Whatever order Looker shows is
// the order Pulse shows.
//
// History, because four passes went the other way: Pulse used to re-rank rows
// it recognised as sales phases (Early Bird → Tier 1…N → Regular) whenever the
// tile's sort didn't already say so, and pivot columns the same way. That is a
// Pulse opinion layered on top of Looker's result — when Looker's own tile
// reads "Regular · Tier 1 · Early Bird" (its sort, or its default sort), Pulse
// read differently, and the report kept coming back as "the order doesn't
// match Looker". It is gone. If a tile's order is wrong in Looker, it is fixed
// in Looker (or with the tile editor's sort) and a re-sync carries it across.
//
// Looker's row order is what Looker RETURNS for the tile's query: Pulse runs
// that query as saved, so normally there is nothing to do here at all. The one
// exception is when Pulse had to run a DIFFERENT sort — the server says so in
// `data.pulse_order` (server/rowOrder.js):
//   · an offset() comparison tile is run current-event-first so its "vs last
//     year" calc can't read backwards (server/query.js currentFirstEventSort);
//   · a row-total sort Looker refuses at the row limit is dropped and the rows
//     re-sorted on the server (server/rowTotalSort.js).
// Every value has already been computed inside its own row by then, so putting
// the rows back in the tile's declared order for DISPLAY changes no number —
// it only undoes the order Pulse needed for the calculation. With no declared
// sort, Looker's own default applies, and is replayed the way Looker documents
// it: the first date dimension descending, else the first measure descending,
// else the first dimension ascending.

const UNKNOWN = 1e6; // sorts last, and stays finite (Infinity − Infinity = NaN)
const ROW_TOTAL = '$$$_row_total_$$$'; // Looker's key for a pivoted row's total

// `["core.tier", "core.date desc"]` → `[{field, desc}]`.
function parseSorts(sorts) {
  return (Array.isArray(sorts) ? sorts : [])
    .map((s) => {
      const raw = String(s == null ? '' : s).trim();
      const m = /\s+(asc|desc)$/i.exec(raw);
      return { field: m ? raw.slice(0, m.index) : raw, desc: !!m && m[1].toLowerCase() === 'desc' };
    })
    .filter((s) => s.field);
}

const isLeaf = (c) => !!c && typeof c === 'object' && !Array.isArray(c) && ('value' in c || 'rendered' in c);

// The comparable value a row holds for one sort key — a leaf cell's typed
// value, or for a pivoted measure its row total (else the sum across pivots),
// which is what Looker sorts a pivoted measure by. Mirrors server/rowOrder.js.
function sortValue(cell) {
  if (cell == null) return null;
  if (isLeaf(cell)) return cell.value == null ? null : cell.value;
  if (typeof cell !== 'object' || Array.isArray(cell)) return cell;
  if (isLeaf(cell[ROW_TOTAL])) return cell[ROW_TOTAL].value == null ? null : cell[ROW_TOTAL].value;
  let sum = null;
  for (const [k, v] of Object.entries(cell)) {
    if (k === ROW_TOTAL || !isLeaf(v)) continue;
    const n = Number(v.value);
    if (Number.isFinite(n)) sum = (sum == null ? 0 : sum) + n;
  }
  return sum;
}

const isBlank = (v) => v == null || v === '';
function compare(a, b) {
  if (typeof a === 'number' && typeof b === 'number') return a < b ? -1 : a > b ? 1 : 0;
  const as = String(a), bs = String(b);
  const na = Number(as), nb = Number(bs);
  if (as.trim() !== '' && bs.trim() !== '' && Number.isFinite(na) && Number.isFinite(nb)) return na < nb ? -1 : na > nb ? 1 : 0;
  return as < bs ? -1 : as > bs ? 1 : 0;
}

// Looker's documented default when a query carries no sort.
function defaultSort(fields) {
  const dims = fields.dimensions || [];
  const isDate = (d) => d && (d.is_timeframe || /^date|^time|^yesno_date/.test(String(d.type || '')));
  const date = dims.find(isDate);
  if (date) return [{ field: date.name, desc: true }];
  const m = (fields.measures || [])[0] || (fields.table_calculations || [])[0];
  if (m) return [{ field: m.name, desc: true }];
  return dims[0] ? [{ field: dims[0].name, desc: false }] : [];
}

// Row order for a tile. `query` is the tile's saved Looker query (omit it and
// rows are returned untouched). `ranSorts` is the server's note of the sort it
// actually ran (`data.pulse_order.sorts`); without one, Pulse ran the tile's
// own query and the rows are already in Looker's order.
export function orderRows(rows, fields = {}, query = null, ranSorts = null) {
  const list = Array.isArray(rows) ? rows : [];
  if (!query || ranSorts == null || list.length < 2) return list;
  const declared = parseSorts(query.sorts);
  const has = (field) => list.some((r) => r && typeof r === 'object' && Object.prototype.hasOwnProperty.call(r, field));
  const keys = (declared.length ? declared : defaultSort(fields || {})).filter((k) => has(k.field));
  if (!keys.length) return list;
  const cols = keys.map((k) => list.map((r) => sortValue(r && r[k.field])));
  return list
    .map((row, i) => ({ row, i }))
    .sort((a, b) => {
      for (let k = 0; k < keys.length; k++) {
        const va = cols[k][a.i], vb = cols[k][b.i];
        // Blanks sort last whichever way the key runs (as server/rowOrder.js).
        if (isBlank(va) || isBlank(vb)) {
          if (isBlank(va) && isBlank(vb)) continue;
          return isBlank(va) ? 1 : -1;
        }
        const d = compare(va, vb);
        if (d) return keys[k].desc ? -d : d;
      }
      return a.i - b.i; // ties keep the order the rows arrived in
    })
    .map((x) => x.row);
}

// Fourth pass (sent back a third time). Every earlier pass fixed the ORDER of the
// rows and was right to — but a horizontal bar chart (`looker_bar`) then drew that
// order upside down. ECharts puts the first category of a vertical category axis
// at the BOTTOM; Looker (Highcharts) puts it at the TOP. So a tile Looker reads
// Early Bird → Tier 1 … → Regular from the top read Regular → … → Early Bird in
// Pulse, however well the rows were sorted — which is why every fix "didn't
// work". Looker's own "Reverse axis" toggle on the category axis
// (`x_axis_reversed` — Looker's x axis is the dimension axis whatever the
// orientation) flips it, and is now honoured on every cartesian chart.
// Returns true when the category axis must be drawn inverted.
export function categoryAxisInverse(visConfig = {}) {
  const horizontal = (visConfig && visConfig.type) === 'looker_bar';
  const reversed = !!(visConfig && visConfig.x_axis_reversed);
  return horizontal !== reversed;
}

// Pivot (series / column-group) order: Looker's hand-arranged `column_order`
// when it has one — that IS the order shown in Looker — otherwise exactly the
// order Looker returned the pivots in (its pivot sort).
//
// Looker names a pivoted column `<pivotKey>_<fieldName>`. Pass the tile's
// measure names and only an EXACT `<key>_<measure>` entry counts — matching on
// the "<key>_" prefix alone let one pivot claim another's entries whenever its
// key starts the other's ("VIP" vs "VIP_Early"). Without names (older
// callers) the prefix match is still used, picking the longest key that fits.
export function orderPivots(pivots, visConfig = {}, measureNames = null) {
  const list = Array.isArray(pivots) ? pivots : [];
  if (list.length < 2) return list;
  const colOrder = Array.isArray(visConfig.column_order) ? visConfig.column_order.map(String) : [];
  if (colOrder.length) {
    const names = Array.isArray(measureNames) && measureNames.length ? measureNames.map(String) : null;
    const keys = list.map((p) => String(p.key));
    const ownerOf = (entry) => {
      if (names) {
        let best = null;
        for (const k of keys) if (names.some((m) => entry === `${k}_${m}`) && (best == null || k.length > best.length)) best = k;
        return best;
      }
      let best = null;
      for (const k of keys) if (entry.startsWith(`${k}_`) && (best == null || k.length > best.length)) best = k;
      return best;
    };
    const firstAt = new Map();
    colOrder.forEach((entry, i) => { const k = ownerOf(entry); if (k != null && !firstAt.has(k)) firstAt.set(k, i); });
    // Trust it only if it actually names these columns — a column_order left
    // over from a different field list must not shuffle anything.
    if (firstAt.size >= 2) return stableBy(list, (p) => (firstAt.has(String(p.key)) ? firstAt.get(String(p.key)) : UNKNOWN));
  }
  return list;
}

// ─── A pivoted table's columns, one by one ───────────────────────────────────
// Looker saves a pivoted table's order COLUMN BY COLUMN: each entry in
// `column_order` is one "<pivotKey>_<field>" column, and the columns of one pivot
// need not sit together. "Ticket Categories" on the Futur 2027 Ticket Overview
// reads KFF27 · Tickets Count | KFF26 · Tickets Count | KFF27 · % change in
// Looker; Pulse used the list only to order whole pivot GROUPS, so every KFF27
// column stayed together and the "% change" landed between the two counts.
//
// `groups` is [{ p: pivot, ms: [measures shown under it] }] in pivot order.
// Returns [{ p, m }] — the flat left-to-right column list. When column_order
// names at least two of these columns exactly, they follow it (columns it
// doesn't name keep their place after the ones it does); otherwise the columns
// stay grouped by pivot, which is Looker's own default.
export function orderPivotColumns(groups, visConfig = {}) {
  const cols = [];
  for (const g of groups || []) for (const m of g.ms || []) cols.push({ p: g.p, m });
  const colOrder = Array.isArray(visConfig && visConfig.column_order) ? visConfig.column_order.map(String) : [];
  if (cols.length < 2 || !colOrder.length) return cols;
  // The tile editor's ↑/↓ writes BARE measure names (Looker never does on a
  // pivoted table). Once it has, that arrangement is the Pulse one the admin
  // chose: keep the pivot groups with the measures in that order
  // (orderPivotMeasures), rather than letting Looker's per-column list undo it.
  const measures = [...new Map(cols.map((c) => [c.m.name, c.m])).values()];
  if (rankBy(measures, colOrder)) return cols;
  const at = new Map();
  colOrder.forEach((c, i) => { if (!at.has(c)) at.set(c, i); });
  const keyOf = (c) => `${c.p.key}_${c.m.name}`;
  if (cols.filter((c) => at.has(keyOf(c))).length < 2) return cols;
  return stableBy(cols, (c) => (at.has(keyOf(c)) ? at.get(keyOf(c)) : UNKNOWN));
}

// Adjacent columns under the same pivot share one header cell, as in Looker —
// a pivot whose columns are split apart gets a header cell for each run.
export function pivotHeaderRuns(cols) {
  const runs = [];
  for (const c of cols || []) {
    const last = runs[runs.length - 1];
    if (last && last.p.key === c.p.key) last.span++;
    else runs.push({ p: c.p, span: 1 });
  }
  return runs;
}

// ─── Pinned columns ───────────────────────────────────────────────────────────
// Looker's grid can freeze columns on the left (`pinned_columns`,
// `{ "<field>": "left" }`), which also moves them to the front of the table,
// in the table's own order. Pulse's first column is already held in view when
// a table scrolls sideways, so drawing the pinned ones first reproduces both.
export function pinnedFirst(list, visConfig = {}) {
  const pinned = visConfig && visConfig.pinned_columns;
  if (!pinned || typeof pinned !== 'object' || Array.isArray(pinned)) return list;
  const isPinned = (c) => c && pinned[c.name] === 'left';
  if (!list.some(isPinned)) return list;
  return [...list.filter(isPinned), ...list.filter((c) => !isPinned(c))];
}

// ─── Table calculations that belong with the dimensions ──────────────────────
// A calc that only uses dimensions (a label, a grouping) is not split per pivot
// in Looker: it is one column on the left, beside the dimensions. Looker's
// result says which calcs those are by their SHAPE — a pivoted measure holds a
// map of pivot key → cell in each row, a dimension-style calc holds one plain
// cell. Drawn as a measure, it repeated under every pivot with blank cells.
const isLeafCell = (c) => !!c && typeof c === 'object' && !Array.isArray(c) && ('value' in c || 'rendered' in c);
export function dimensionLikeCalcs(calcs, rows) {
  const sample = (Array.isArray(rows) ? rows : []).slice(0, 20);
  return (calcs || []).filter((c) => c && c.name && sample.some((r) => r && isLeafCell(r[c.name])));
}

// ─── Column order (ticket #68, sent back: "the order of the data keeps being
// messed up … adds [columns] in a different order that I can't correct here") ──
// Every renderer used to draw a table's columns in one fixed order — all the
// dimensions, then all the measures, then all the table calculations — whatever
// Looker showed. In Looker the columns are dragged into place: a "% change" calc
// between two measures, a measure ahead of a dimension, measures reordered under
// each pivot. That order lives in two places, and Pulse now reads both:
//   1. `vis_config.column_order` — the table's saved, hand-arranged order. Flat
//      tables name columns bare ("orders.count"); pivoted tables name each one
//      under its pivot ("2025_orders.count"). The tile editor's ↑/↓ writes bare
//      names here too, so a column can be put back in place without Looker.
//   2. the query's `fields` — the order the columns were picked in, which is the
//      table's order whenever nothing was dragged.
// A list is only trusted when it names at least two of the columns being
// ordered (a column_order left over from a different field list must not
// shuffle anything), and anything a list doesn't name keeps its own place after
// the ones it does.

function rankBy(list, names) {
  if (!Array.isArray(names) || !names.length) return null;
  const at = new Map();
  names.forEach((n, i) => { const k = String(n); if (!at.has(k)) at.set(k, i); });
  const hits = list.filter((c) => c && at.has(c.name)).length;
  return hits >= 2 ? (c) => (c && at.has(c.name) ? at.get(c.name) : UNKNOWN) : null;
}

// Columns of ONE kind (dimensions, or measures + calcs) in Looker's order.
function orderKind(list, visConfig, query) {
  const rank = rankBy(list, visConfig && visConfig.column_order) || rankBy(list, query && query.fields);
  return rank ? stableBy(list, (c) => rank(c)) : list;
}

// A table's columns in the order Looker draws them. `dimensions` / `measures`
// are what the tile shows (lib/visibleFields.mjs — measures with the calcs
// folded in). Returns each kind in order, plus `all`: the flat table's full
// left-to-right order, which may mix the kinds when column_order says so.
export function orderColumns(dimensions = [], measures = [], visConfig = {}, query = null) {
  const dims = pinnedFirst(orderKind(dimensions || [], visConfig, query), visConfig);
  const meas = orderKind(measures || [], visConfig, query);
  const kindOrder = [...dims, ...meas];
  const rank = rankBy(kindOrder, visConfig && visConfig.column_order);
  return { dimensions: dims, measures: meas, all: pinnedFirst(rank ? stableBy(kindOrder, (c) => rank(c)) : kindOrder, visConfig) };
}

// The measures under ONE pivot column. A bare-name order (set in the tile
// editor) wins; then Looker's own per-pivot entries ("<pivotKey>_<measure>");
// otherwise the measures keep the order orderColumns gave them.
export function orderPivotMeasures(measures, visConfig = {}, pivotKey = '') {
  const list = Array.isArray(measures) ? measures : [];
  if (list.length < 2) return list;
  const colOrder = Array.isArray(visConfig && visConfig.column_order) ? visConfig.column_order : [];
  if (rankBy(list, colOrder)) return list; // already ordered by the bare names
  const prefix = `${pivotKey}_`;
  const under = colOrder.filter((c) => String(c).startsWith(prefix)).map((c) => String(c).slice(prefix.length));
  const rank = rankBy(list, under);
  return rank ? stableBy(list, (c) => rank(c)) : list;
}

// The tile editor's column list (raw names), in the tile's current order.
export function orderColumnNames(names, visConfig = {}, query = null) {
  const cols = (names || []).map((name) => ({ name }));
  return orderKind(cols, visConfig, query).map((c) => c.name);
}

// Move one column up/down and return the vis's new `column_order`: the editor's
// full list (bare names) in its new order, followed by whatever else Looker had
// saved there (its per-pivot entries, the row-number column) so nothing it
// arranged is lost.
export function moveColumn(names, name, delta, visConfig = {}) {
  const list = [...(names || [])];
  const i = list.indexOf(name);
  const j = i + delta;
  if (i < 0 || j < 0 || j >= list.length) return null;
  [list[i], list[j]] = [list[j], list[i]];
  const prev = Array.isArray(visConfig && visConfig.column_order) ? visConfig.column_order : [];
  const mine = new Set(list);
  return [...list, ...prev.filter((c) => !mine.has(c))];
}

export function pivotLabel(p) {
  if (!p) return '';
  return p.data ? Object.values(p.data).join(' / ') : String(p.key ?? '');
}

// Sort without losing the incoming order of equal-ranked items — that order is
// Looker's, and it's the tie-break we want (e.g. events inside one tier).
function stableBy(list, keyOf) {
  return list
    .map((item, i) => ({ item, i, k: keyOf(item, i) }))
    .sort((a, b) => (a.k - b.k) || (a.i - b.i))
    .map((d) => d.item);
}
